import { Modal } from '../../../components/modal/modal';
import { useState } from 'react';
import { Spinner } from '../../../components/spinner/spinner';
import { FieldLabel, FileField } from '../../../components/field/field';
import { Location } from '../../../clients/quipli-service/location/location';
import FileSaver from 'file-saver';
import { DateTime } from 'luxon';
import { useImportExportApi } from '../../../clients/quipli-service/import-export/import-export-api';
import { Typography } from '../../../components/typography/typography';
import { LocationSelect } from '../../location/select/location-select';

export const ServiceImportExportModal = ({
  isOpen,
  isImport,
  setIsOpen,
  onFileUpload,
  onFileError,
}: {
  isOpen: boolean;
  isImport: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onFileUpload: () => void;
  onFileError: (message: string) => void;
}) => {
  const importExportAPI = useImportExportApi();

  // React states
  const [isRequestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>();
  const [file, setFile] = useState<File>();

  // Callbacks
  const exportParts = async () => {
    if (!selectedLocation) return;

    setRequestInProgress(true);
    const file = await importExportAPI.exportLocationServices(selectedLocation.id);
    const locationName = selectedLocation?.name.split(' ').join('_');

    const exportFileName = `Services_${locationName}_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const data = new Blob([file], { type: fileType });
    FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);

    setRequestInProgress(false);

    // Close modal
    setIsOpen(false);
    clear();
  };

  const importParts = async () => {
    if (!selectedLocation || !file) return;

    setRequestInProgress(true);

    if (file) {
      try {
        await importExportAPI.importLocationServices(selectedLocation.id, file);
        onFileUpload();
      } catch (error) {
        onFileError('Error importing services');
        setRequestInProgress(false);
        return;
      }
    }

    setRequestInProgress(false);

    // Close modal
    setIsOpen(false);
    clear();
  };

  const clear = () => {
    setSelectedLocation(undefined);
    setFile(undefined);
  };

  const isButtonDisabled = (): boolean => {
    return isRequestInProgress || !selectedLocation || (isImport && !file);
  };

  const closeModal = () => {
    // Don't close modal if there is a request in progress
    if (isRequestInProgress) return;

    clear();
    setIsOpen(false);
  };

  const exportSampleFile = async () => {
    const file = await importExportAPI.exportLocationServicesSample();

    const exportFileName = `Services_Sample_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const data = new Blob([file], { type: fileType });
    FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
  };

  return (
    <Modal
      isOpen={isOpen}
      title={isImport ? 'Import Services' : 'Export Services'}
      actions={
        isRequestInProgress ? (
          <Spinner />
        ) : (
          <>
            <button
              className="button button-small button-block button-secondary"
              disabled={isRequestInProgress}
              onClick={() => closeModal()}
            >
              Cancel
            </button>

            <button
              className="button button-small button-block button-primary"
              disabled={isButtonDisabled()}
              onClick={() => (isImport ? importParts() : exportParts())}
            >
              {isImport ? 'Import File' : 'Export'}
            </button>
          </>
        )
      }
      close={closeModal}
    >
      <div>
        {isImport && (
          <>
            <div className="text-center px-4 mb-4">
              <Typography type="text-sm" className="text-grey-500">
                <div>Import services from a XLS or XLSX spreadsheet file.</div>
                <div>
                  Click{' '}
                  <a className="text-quipli-black-800 underline cursor-pointer" onClick={exportSampleFile}>
                    here
                  </a>{' '}
                  to see the exact format of the worksheet.
                </div>
              </Typography>
            </div>
            <div className="text-center px-4 mb-4">
              <Typography type="text-sm" className="text-grey-500">
                <div>
                  Sale stock values are additive. The value on the spreadsheet will be added to the existing location
                  stock.
                </div>
              </Typography>
            </div>
          </>
        )}

        {/* Location */}
        <div className="flex flex-col gap-[4px]">
          <FieldLabel label="Location" isRequired />
          <LocationSelect setLocation={setSelectedLocation} dataCy="location-select" />
        </div>

        {/* File */}
        {isImport && (
          <div className="mt-4">
            <FileField
              className="w-full h-full resize-none text-sm"
              label="File"
              value={file}
              accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={(newFile) => setFile(newFile as File)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
